import { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  OutlinedInput,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { Close, Search } from "@mui/icons-material";
import { apiRequest } from "../../../services/apiService";
import { getCssVariableHandler } from "../../../utils/styles/getCssVariables";
import {
  EMPTY_COORDINATOR_FILTER,
  coordinatorDisplayName,
  safeAvatarSrc,
} from "./dashboardCoordinators";
import type {
  CoordinatorFilterValue,
  CoordinatorMatch,
  DashboardCoordinator,
} from "./dashboardCoordinators";

const EMPTY_SELECTION: DashboardCoordinator[] = [];

/** Rows shown before the list scrolls (dense row with name + email ≈ 59px). */
const VISIBLE_ROWS = 6;
const LIST_ROW_PX = 59;

// Session cache of the active coordinators (sorted by name). `null` result =
// request failed; a failure is not cached so the next open retries.
let coordinatorsCache: DashboardCoordinator[] | null = null;
let coordinatorsInflight: Promise<DashboardCoordinator[] | null> | null = null;

function loadActiveCoordinators(): Promise<DashboardCoordinator[] | null> {
  if (coordinatorsCache) return Promise.resolve(coordinatorsCache);
  if (coordinatorsInflight) return coordinatorsInflight;
  coordinatorsInflight = apiRequest({
    method: "GET",
    url: "getActiveCoordinators?search=",
    data: null,
    isToken: true,
  })
    .then(({ response, error }) => {
      const rows = response?.status === 200 ? response?.data?.data : null;
      if (!Array.isArray(rows) || error) return null;
      coordinatorsCache = [...rows].sort((a, b) =>
        coordinatorDisplayName(a).localeCompare(coordinatorDisplayName(b)),
      );
      return coordinatorsCache;
    })
    .catch(() => null)
    .finally(() => {
      coordinatorsInflight = null;
    });
  return coordinatorsInflight;
}

const pink = () =>
  getCssVariableHandler({ variableName: "--primary-pink-hex" });
const pinkLight = () =>
  getCssVariableHandler({ variableName: "--primary-pink-rgb-light" });
const white = () => getCssVariableHandler({ variableName: "--white-rgb" });

type Props = {
  value: CoordinatorFilterValue;
  onChange: (next: CoordinatorFilterValue) => void;
  /** Whether the panel is visible — the coordinator list is fetched on first show. */
  active?: boolean;
  /** Focus the search box when shown (off when embedded in another menu). */
  autoFocus?: boolean;
  /** Hide the trailing "clear all" (the host menu has its own). */
  hideClear?: boolean;
};

/**
 * Coordinator filter body: search box, selected chips, Any/All switch and the
 * checkbox list of active coordinators. Used by the "Coordinators" column
 * header popover and embedded in the toolbar funnel menu — both edit the same
 * dashboard state, so they are always in sync.
 */
export default function CoordinatorFilterPanel({
  value,
  onChange,
  active = true,
  autoFocus = true,
  hideClear = false,
}: Props) {
  const [options, setOptions] = useState<DashboardCoordinator[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [query, setQuery] = useState("");

  const selected = value?.selected ?? EMPTY_SELECTION;
  const match: CoordinatorMatch = value?.match === "all" ? "all" : "any";
  const selectedIds = useMemo(
    () => new Set(selected.map((c) => c._id)),
    [selected],
  );

  // Active coordinators are loaded the first time the panel shows (cached for
  // the page session — the toolbar menu remounts this panel on every open),
  // then filtered locally so typing is instant. Only `active` drives the
  // effect: re-running it on `loading` would cancel the in-flight request.
  useEffect(() => {
    if (!active || options !== null) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    loadActiveCoordinators()
      .then((rows) => {
        if (cancelled) return;
        if (!rows) setLoadError(true);
        setOptions(rows ?? []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = options ?? [];
    if (!q) return all;
    const terms = q.split(/\s+/).filter(Boolean);
    return all.filter((c) => {
      const hay = `${coordinatorDisplayName(c)} ${c.email ?? ""}`.toLowerCase();
      return terms.every((t) => hay.includes(t));
    });
  }, [options, query]);

  const toggle = (c: DashboardCoordinator) => {
    const next = selectedIds.has(c._id)
      ? selected.filter((s) => s._id !== c._id)
      : [
          ...selected,
          {
            _id: c._id,
            fullName: c.fullName,
            email: c.email,
            profileImage: c.profileImage,
          },
        ];
    onChange({ selected: next, match });
  };

  const setMatch = (nextMatch: CoordinatorMatch) =>
    onChange({ selected, match: nextMatch });

  const clearAll = () => {
    setQuery("");
    // Same constant the dashboard resets to: no state change (and no refetch)
    // when there was nothing selected.
    if (selected.length || match !== "any") onChange(EMPTY_COORDINATOR_FILTER);
  };

  return (
    <Box sx={{ color: white() }}>
      <Box sx={{ p: 1.5, pb: 1 }}>
        <OutlinedInput
          autoFocus={autoFocus}
          fullWidth
          size="small"
          placeholder="Search coordinator"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          // Inside a MUI Menu the list's type-ahead would otherwise eat keystrokes.
          onKeyDown={(e) => e.stopPropagation()}
          startAdornment={
            <InputAdornment position="start">
              <Search fontSize="small" sx={{ color: white() }} />
            </InputAdornment>
          }
          endAdornment={
            query ? (
              <InputAdornment position="end">
                <Close
                  fontSize="small"
                  sx={{ cursor: "pointer", color: white() }}
                  onClick={() => setQuery("")}
                />
              </InputAdornment>
            ) : null
          }
          sx={{
            color: white(),
            height: 36,
            borderRadius: "6px",
            "& input::placeholder": { color: white(), opacity: 0.6 },
            "& fieldset": { borderColor: `${white()} !important` },
            "&:hover fieldset, &.Mui-focused fieldset": {
              borderColor: `${pink()} !important`,
            },
          }}
        />

        {selected.length ? (
          <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
            {selected.map((c) => (
              <Chip
                key={c._id}
                size="small"
                variant="outlined"
                label={coordinatorDisplayName(c)}
                avatar={
                  <Avatar src={safeAvatarSrc(c.profileImage)}>
                    {coordinatorDisplayName(c)[0]?.toUpperCase()}
                  </Avatar>
                }
                onDelete={() => toggle(c)}
                sx={{
                  color: white(),
                  borderColor: pink(),
                  "& .MuiChip-deleteIcon": {
                    color: white(),
                    opacity: 0.7,
                    "&:hover": { color: pink(), opacity: 1 },
                  },
                }}
              />
            ))}
          </Stack>
        ) : null}

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mt: 1 }}
        >
          <Typography variant="body2" sx={{ opacity: 0.85 }}>
            {match === "all"
              ? "Show has ALL selected"
              : "Show has ANY selected"}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography
              variant="caption"
              sx={{ fontWeight: match === "any" ? 700 : 400 }}
            >
              Any
            </Typography>
            <Switch
              size="small"
              color="secondary"
              checked={match === "all"}
              onChange={(e) => setMatch(e.target.checked ? "all" : "any")}
              inputProps={{ "aria-label": "Match all selected coordinators" }}
              sx={{
                "& .MuiSwitch-switchBase": { color: white() },
                "& .MuiSwitch-track": {
                  backgroundColor: white(),
                  opacity: 0.4,
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{ fontWeight: match === "all" ? 700 : 400 }}
            >
              All
            </Typography>
          </Stack>
        </Stack>
      </Box>

      <Divider sx={{ borderColor: pink() }} />

      <List
        dense
        disablePadding
        className="custom-scroll-design"
        sx={{ maxHeight: LIST_ROW_PX * VISIBLE_ROWS, overflowY: "auto" }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={22} color="secondary" />
          </Box>
        ) : loadError ? (
          <Typography variant="body2" sx={{ p: 1.5, opacity: 0.8 }}>
            Could not load coordinators.
          </Typography>
        ) : filtered.length === 0 ? (
          <Typography variant="body2" sx={{ p: 1.5, opacity: 0.8 }}>
            No coordinator matches.
          </Typography>
        ) : (
          filtered.map((c) => {
            const name = coordinatorDisplayName(c);
            const checked = selectedIds.has(c._id);
            return (
              <ListItemButton
                key={c._id}
                dense
                selected={checked}
                onClick={() => toggle(c)}
                sx={{
                  color: white(),
                  "&.Mui-selected": { backgroundColor: pinkLight() },
                  "&:hover, &.Mui-selected:hover": {
                    backgroundColor: pink(),
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 34 }}>
                  <Checkbox
                    edge="start"
                    size="small"
                    checked={checked}
                    tabIndex={-1}
                    sx={{
                      color: white(),
                      "&.Mui-checked": { color: pink() },
                    }}
                  />
                </ListItemIcon>
                <Avatar
                  src={safeAvatarSrc(c.profileImage)}
                  sx={{
                    width: 24,
                    height: 24,
                    mr: 1,
                    fontSize: "0.7rem",
                    bgcolor: "#333",
                    color: white(),
                  }}
                >
                  {name[0]?.toUpperCase()}
                </Avatar>
                <ListItemText
                  primary={name}
                  secondary={c.email}
                  primaryTypographyProps={{ noWrap: true }}
                  secondaryTypographyProps={{
                    noWrap: true,
                    sx: { color: white(), opacity: 0.65, fontSize: "12px" },
                  }}
                />
              </ListItemButton>
            );
          })
        )}
      </List>

      {hideClear ? null : (
        <>
          <Divider sx={{ borderColor: pink() }} />
          <Typography
            onClick={clearAll}
            sx={{
              fontSize: "0.8rem",
              px: "1rem",
              py: "0.4rem",
              textAlign: "right",
              cursor: "pointer",
              userSelect: "none",
              "&:hover": { textDecoration: "underline" },
              "&:active": { color: pink() },
            }}
          >
            clear all
          </Typography>
        </>
      )}
    </Box>
  );
}
